import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import api from "@/lib/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | success | failed | timeout
  const [listingId, setListingId] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("failed"); return; }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      if (attempts.current >= 6) { setState("timeout"); return; }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        setListingId(data.listing_id);
        if (data.payment_status === "paid") { setState("success"); return; }
        if (data.status === "expired") { setState("failed"); return; }
        setTimeout(poll, 2000);
      } catch {
        if (!cancelled) setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Header />
      <div className="flex-1 grid place-items-center px-4 py-16">
        <div className="bg-white border border-slate-200 rounded-xl p-10 max-w-md w-full text-center" data-testid="payment-result">
          {state === "checking" && (
            <>
              <Loader2 className="mx-auto text-emerald-600 animate-spin" size={48} />
              <h1 className="font-heading text-2xl font-bold text-slate-900 mt-4">Confirming your payment…</h1>
              <p className="text-slate-500 mt-2">Please wait while we verify your €5 listing fee.</p>
            </>
          )}
          {state === "success" && (
            <>
              <CheckCircle2 className="mx-auto text-emerald-600" size={48} />
              <h1 className="font-heading text-2xl font-bold text-slate-900 mt-4" data-testid="payment-success">Payment successful!</h1>
              <p className="text-slate-500 mt-2">Your ad is now live on DealShed.</p>
              <div className="flex gap-2 mt-6">
                <Button onClick={() => navigate(listingId ? `/listing/${listingId}` : "/dashboard")} data-testid="view-listing-button" className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white">View my ad</Button>
                <Button variant="outline" onClick={() => navigate("/dashboard")} className="flex-1">My DealShed</Button>
              </div>
            </>
          )}
          {(state === "failed" || state === "timeout") && (
            <>
              <XCircle className="mx-auto text-red-500" size={48} />
              <h1 className="font-heading text-2xl font-bold text-slate-900 mt-4" data-testid="payment-failed">{state === "timeout" ? "Still processing" : "Payment not completed"}</h1>
              <p className="text-slate-500 mt-2">{state === "timeout" ? "We couldn't confirm your payment yet. Check My DealShed shortly." : "Your payment was cancelled or failed. Your ad is saved and you can try again."}</p>
              <div className="flex gap-2 mt-6">
                <Button onClick={() => navigate("/dashboard")} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white">Go to My DealShed</Button>
                <Button variant="outline" onClick={() => navigate("/place-ad")} className="flex-1">Place an Ad</Button>
              </div>
            </>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
